import { useEffect, useState } from 'react'
import { Building2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { fetchCompany, updateCompany } from '@/lib/api/company'
import { cn } from '@/lib/utils'

const CURRENCIES = ['AED', 'SAR', 'USD', 'EUR', 'GBP', 'QAR', 'KWD', 'OMR', 'BHD']

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

export function CompanySetupPage() {
  const { refreshUser } = useAuth()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState({
    name: '',
    legal_name: '',
    base_currency: 'AED',
    logo: '',
    is_active: true,
  })

  useEffect(() => {
    let active = true

    async function loadCompany() {
      setLoading(true)
      setError('')
      try {
        const company = await fetchCompany()
        if (!active) return
        setForm({
          name: company.name ?? '',
          legal_name: company.legal_name ?? '',
          base_currency: company.base_currency ?? 'AED',
          logo: company.logo ?? '',
          is_active: Boolean(company.is_active),
        })
      } catch (err) {
        if (!active) return
        setError(err?.response?.data?.message ?? 'Unable to load company profile.')
      } finally {
        if (active) setLoading(false)
      }
    }

    loadCompany()
    return () => {
      active = false
    }
  }, [])

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
    setSuccess('')
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    try {
      await updateCompany({
        name: form.name,
        legal_name: form.legal_name,
        base_currency: form.base_currency,
        logo: form.logo || null,
        is_active: form.is_active,
      })
      await refreshUser()
      setSuccess('Company profile saved.')
    } catch (err) {
      const message =
        getFieldError(err, 'name') ??
        getFieldError(err, 'legal_name') ??
        getFieldError(err, 'base_currency') ??
        err?.response?.data?.message ??
        'Unable to save company profile.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-8 lg:px-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Building2 className="h-5 w-5" aria-hidden />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Company setup</h1>
          <p className="text-sm text-muted-foreground">
            Legal identity, branding, and base currency for your tenant.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="card-surface mx-auto max-w-2xl p-6 lg:p-8">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading company profile…</p>
        ) : (
          <div className="space-y-5">
            <div>
              <label htmlFor="company-name" className="mb-1.5 block text-sm font-medium">
                Trading name
              </label>
              <input
                id="company-name"
                type="text"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              />
            </div>

            <div>
              <label htmlFor="company-legal-name" className="mb-1.5 block text-sm font-medium">
                Legal name
              </label>
              <input
                id="company-legal-name"
                type="text"
                value={form.legal_name}
                onChange={(e) => updateField('legal_name', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                required
              />
            </div>

            <div>
              <label htmlFor="company-currency" className="mb-1.5 block text-sm font-medium">
                Base currency
              </label>
              <select
                id="company-currency"
                value={form.base_currency}
                onChange={(e) => updateField('base_currency', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              >
                {CURRENCIES.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="company-logo" className="mb-1.5 block text-sm font-medium">
                Logo URL
              </label>
              <input
                id="company-logo"
                type="url"
                value={form.logo}
                onChange={(e) => updateField('logo', e.target.value)}
                placeholder="https://…"
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => updateField('is_active', e.target.checked)}
                className="rounded border-border"
              />
              Company is active
            </label>

            {error ? (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}

            {success ? (
              <p className="text-sm text-primary" role="status">
                {success}
              </p>
            ) : null}

            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save company'}
            </Button>
          </div>
        )}
      </form>
    </div>
  )
}
